import { queryOptions, useMutation, useQueryClient } from "@tanstack/react-query";

import {
  getSponsorAuthSession,
  getSponsorSponsors,
  getSponsorSponsorsByPlatformSponsorId,
  getSponsorSponsorsByPlatformSponsorIdMembers,
  postSponsorSponsorsByPlatformSponsorIdBillingPortalSession,
  postSponsorSponsorsByPlatformSponsorIdMembersByPlatformUserMembershipIdFreezeRequests,
  putSponsorSponsorsByPlatformSponsorIdMembersByPlatformUserMembershipIdUnenroll,
} from "api/methods";

export const sponsorPortalKeys = {
  all: ["sponsorPortal"] as const,
  session: () => [...sponsorPortalKeys.all, "session"] as const,
  sponsors: () => [...sponsorPortalKeys.all, "sponsors"] as const,
  sponsor: (platformSponsorId: string) =>
    [...sponsorPortalKeys.all, "sponsor", platformSponsorId] as const,
  members: (platformSponsorId: string) =>
    [...sponsorPortalKeys.all, "members", platformSponsorId] as const,
};

/**
 * Resolves the caller's sponsor session from whatever token is already on the axios instance.
 *
 * Retries are off on purpose: a 403 here is the definitive "you administer no sponsor" answer that
 * sends the visitor to sign in, and retrying it three times would just delay that redirect.
 */
export const sponsorSessionQuery = () =>
  queryOptions({
    queryKey: sponsorPortalKeys.session(),
    queryFn: () => getSponsorAuthSession(),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });

export const sponsorsQuery = () =>
  queryOptions({
    queryKey: sponsorPortalKeys.sponsors(),
    queryFn: () => getSponsorSponsors(),
  });

export const sponsorQuery = (platformSponsorId: string) =>
  queryOptions({
    queryKey: sponsorPortalKeys.sponsor(platformSponsorId),
    queryFn: () => getSponsorSponsorsByPlatformSponsorId({ platformSponsorId }),
  });

export const sponsorMembersQuery = (platformSponsorId: string) =>
  queryOptions({
    queryKey: sponsorPortalKeys.members(platformSponsorId),
    queryFn: () => getSponsorSponsorsByPlatformSponsorIdMembers({ platformSponsorId }),
  });

/**
 * Unenrols a member. Both the member list and the sponsor itself are invalidated because the
 * sponsor's headline member count and monthly cost are derived from the same enrolments — leaving
 * the summary cached would show a cost that no longer matches the rows underneath it.
 */
export const useUnenrolMemberMutation = (platformSponsorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (platformUserMembershipId: string) =>
      putSponsorSponsorsByPlatformSponsorIdMembersByPlatformUserMembershipIdUnenroll({
        platformSponsorId,
        platformUserMembershipId,
      }),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: sponsorPortalKeys.members(platformSponsorId) }),
        queryClient.invalidateQueries({ queryKey: sponsorPortalKeys.sponsor(platformSponsorId) }),
        queryClient.invalidateQueries({ queryKey: sponsorPortalKeys.sponsors() }),
      ]);
    },
  });
};

export const useFreezeMemberMutation = (platformSponsorId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      platformUserMembershipId,
      billingPeriods,
      requestReason,
    }: {
      platformUserMembershipId: string;
      billingPeriods: number;
      requestReason?: string;
    }) =>
      postSponsorSponsorsByPlatformSponsorIdMembersByPlatformUserMembershipIdFreezeRequests({
        platformSponsorId,
        platformUserMembershipId,
        billingPeriods,
        requestReason,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: sponsorPortalKeys.members(platformSponsorId),
      });
    },
  });
};

/**
 * Mints a hosted billing-portal link. Not a query: the URL is single-use and short-lived, so it
 * must never be cached or replayed — every visit asks for a fresh one.
 */
export const useBillingPortalMutation = (platformSponsorId: string) =>
  useMutation({
    mutationFn: () =>
      postSponsorSponsorsByPlatformSponsorIdBillingPortalSession({ platformSponsorId }),
  });
